import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Rectangle, Polygon, Popup, ZoomControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import * as turf from '@turf/turf';
import type { GridCell, LayerSettings } from '../types';
import { GridCellPopup } from './GridCellPopup';
import { clusterCells } from '../utils/gridGenerator';
import { MagicSelectControl } from './MagicSelectControl';
import './Map.css';

// Polygon category types
export type PolygonCategory = 'forest' | 'reforestation' | 'bio-agro';

const CATEGORY_COLORS: Record<PolygonCategory, { stroke: string; fill: string; label: string }> = {
  'forest': { stroke: '#228B22', fill: '#228B22', label: 'Forest' },
  'reforestation': { stroke: '#90EE90', fill: '#90EE90', label: 'Reforestation' },
  'bio-agro': { stroke: '#DAA520', fill: '#DAA520', label: 'Bio Agro' }
};

// Project polygon interface
export interface ProjectPolygon {
  id: string;
  layerId: number;
  name: string;
  category?: PolygonCategory;
  coordinates: [number, number][];
  areaHectares: number;
  territoryId: number;
  territoryName?: string;
  createdAt: Date;
}

// Convert hex color to KML AABBGGRR format
function hexToKml(hex: string, alpha = 'ff'): string {
  const r = hex.slice(1, 3);
  const g = hex.slice(3, 5);
  const b = hex.slice(5, 7);
  return `${alpha}${b}${g}${r}`;
}

// Generate KML from project polygons
function generateKML(polygons: ProjectPolygon[], territoryName?: string): string {
  const placemarks = polygons.map((polygon) => {
    const coordinates = polygon.coordinates
      .map(coord => `${coord[1]},${coord[0]},0`)
      .join(' ');
    const closedCoords = coordinates + ` ${polygon.coordinates[0][1]},${polygon.coordinates[0][0]},0`;

    const cat = polygon.category ? CATEGORY_COLORS[polygon.category] : null;
    const strokeColor = cat ? hexToKml(cat.stroke) : 'ff00ff00';
    const fillColor = cat ? hexToKml(cat.fill, '4d') : '4d00ff00';
    const categoryText = polygon.category ? CATEGORY_COLORS[polygon.category].label : 'Uncategorized';

    return `
    <Placemark>
      <name>${polygon.name}</name>
      <description>
        <![CDATA[
          <p><strong>Category:</strong> ${categoryText}</p>
          <p><strong>Area:</strong> ${polygon.areaHectares.toLocaleString('en-US', { maximumFractionDigits: 2 })} hectares</p>
          <p><strong>Territory:</strong> ${polygon.territoryName || polygon.territoryId}</p>
          <p><strong>Created:</strong> ${polygon.createdAt.toISOString()}</p>
        ]]>
      </description>
      <Style>
        <LineStyle>
          <color>${strokeColor}</color>
          <width>3</width>
        </LineStyle>
        <PolyStyle>
          <color>${fillColor}</color>
        </PolyStyle>
      </Style>
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>${closedCoords}</coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Project Areas${territoryName ? ` - ${territoryName}` : ''}</name>
    <description>Project polygons exported from GPC Territory Map</description>
    ${placemarks}
  </Document>
</kml>`;
}

// Generate KML from territory boundaries (multi-ring polygons)
function generateBoundaryKML(boundaries: TerritoryBoundary[]): string {
  const placemarks = boundaries.map(territory => {
    const polygons = territory.boundary.map(ring => {
      if (ring.length === 0) return '';
      const coords = ring.map(c => `${c[1]},${c[0]},0`).join(' ');
      const closed = `${coords} ${ring[0][1]},${ring[0][0]},0`;
      return `
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>${closed}</coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>`;
    }).filter(Boolean).join('\n');

    const geometry = territory.boundary.length > 1
      ? `<MultiGeometry>${polygons}</MultiGeometry>`
      : polygons;

    return `
    <Placemark>
      <name>${territory.name}</name>
      <description>Territory code: ${territory.id}</description>
      <Style>
        <LineStyle>
          <color>ff61a9c9</color>
          <width>3</width>
        </LineStyle>
        <PolyStyle>
          <color>1561a9c9</color>
        </PolyStyle>
      </Style>
      ${geometry}
    </Placemark>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Territory Boundaries</name>
    <description>Territory boundaries exported from GPC Territory Map</description>
    ${placemarks}
  </Document>
</kml>`;
}

// Color palette for multiple territory boundaries
const BOUNDARY_COLORS = [
  { stroke: '#c9a961', fill: '#c9a961' },  // Gold (default)
  { stroke: '#4299e1', fill: '#4299e1' },  // Blue
  { stroke: '#48bb78', fill: '#48bb78' },  // Green
  { stroke: '#ed8936', fill: '#ed8936' },  // Orange
  { stroke: '#9f7aea', fill: '#9f7aea' },  // Purple
  { stroke: '#f56565', fill: '#f56565' },  // Red
  { stroke: '#38b2ac', fill: '#38b2ac' },  // Teal
  { stroke: '#ed64a6', fill: '#ed64a6' },  // Pink
];

export interface TerritoryBoundary {
  id: number;
  name: string;
  boundary: [number, number][][];
}

interface MapProps {
  center: [number, number];
  boundaries: TerritoryBoundary[];
  gridCells: GridCell[];
  layers: LayerSettings;
  onMapClick?: (lat: number, lng: number) => void;
  selectedTerritoryId?: number;
  onProjectPolygonCreate?: (polygon: ProjectPolygon) => void;
}

// Draw Control Component for polygon drawing
// Track whether Leaflet.Draw is actively drawing/editing (shared via ref for MapEventHandler)
const drawingActiveRef = { current: false };

const DrawControl: React.FC<{
  selectedTerritoryId?: number;
  onPolygonCreated: (coords: [number, number][], areaHectares: number, layerId: number) => void;
  onPolygonEdited: (layerId: number, coords: [number, number][], areaHectares: number) => void;
  onPolygonDeleted: (layerId: number) => void;
}> = ({ selectedTerritoryId, onPolygonCreated, onPolygonEdited, onPolygonDeleted }) => {
  const map = useMap();
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  const featureGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const selectedTerritoryRef = useRef(selectedTerritoryId);

  // Keep ref updated
  useEffect(() => {
    selectedTerritoryRef.current = selectedTerritoryId;
  }, [selectedTerritoryId]);

  useEffect(() => {
    // Add feature group to map
    map.addLayer(featureGroupRef.current);

    // Create draw control - always show polygon tool
    drawControlRef.current = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: {
            color: '#00FF00',
            weight: 3,
            fillColor: '#00FF00',
            fillOpacity: 0.3
          }
        },
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
        marker: false
      },
      edit: {
        featureGroup: featureGroupRef.current,
        remove: true
      }
    });

    map.addControl(drawControlRef.current);

    // Track draw/edit active state so MapEventHandler can skip clicks
    const onDrawStart = () => {
      drawingActiveRef.current = true;
      if (!selectedTerritoryRef.current) {
        if (drawControlRef.current) {
          (drawControlRef.current as any)._toolbars.draw._modes.polygon.handler.disable();
        }
        drawingActiveRef.current = false;
      }
    };
    const onDrawStop = () => { drawingActiveRef.current = false; };
    const onEditStart = () => { drawingActiveRef.current = true; };
    const onEditStop = () => { drawingActiveRef.current = false; };
    const onDeleteStart = () => { drawingActiveRef.current = true; };
    const onDeleteStop = () => { drawingActiveRef.current = false; };

    // Helper to calculate area
    const calculateArea = (latlngs: L.LatLng[]) => {
      const coords: [number, number][] = latlngs.map((ll: L.LatLng) => [ll.lat, ll.lng]);
      const turfCoords = coords.map(c => [c[1], c[0]]);
      turfCoords.push(turfCoords[0]);
      const polygon = turf.polygon([turfCoords]);
      const areaSquareMeters = turf.area(polygon);
      return { coords, areaHectares: areaSquareMeters / 10000, areaSquareMeters };
    };

    // Helper to update popup
    const updatePopup = (layer: L.Layer, areaHectares: number, areaSquareMeters: number) => {
      if (layer instanceof L.Polygon) {
        layer.unbindPopup();
        layer.bindPopup(`
          <div style="padding: 8px;">
            <strong style="color: #1a2642; font-size: 14px;">Project Area</strong>
            <div style="margin-top: 8px; font-size: 16px; color: #00AA00; font-weight: bold;">
              ${areaHectares.toLocaleString('en-US', { maximumFractionDigits: 2 })} hectares
            </div>
            <div style="margin-top: 4px; font-size: 12px; color: #666;">
              (${(areaSquareMeters / 1000000).toLocaleString('en-US', { maximumFractionDigits: 4 })} km²)
            </div>
          </div>
        `);
      }
    };

    // Handle polygon creation
    const onCreated = (e: any) => {
      if (!selectedTerritoryRef.current) {
        return;
      }

      const layer = e.layer;
      featureGroupRef.current.addLayer(layer);

      const latlngs = layer.getLatLngs()[0];
      const { coords, areaHectares, areaSquareMeters } = calculateArea(latlngs);

      updatePopup(layer, areaHectares, areaSquareMeters);
      layer.openPopup();

      // Get Leaflet's internal layer ID
      const layerId = L.Util.stamp(layer);
      onPolygonCreated(coords, areaHectares, layerId);
    };

    // Handle polygon editing
    const onEdited = (e: any) => {
      const layers = e.layers;
      layers.eachLayer((layer: any) => {
        const layerId = L.Util.stamp(layer);
        const latlngs = layer.getLatLngs()[0];
        const { coords, areaHectares, areaSquareMeters } = calculateArea(latlngs);

        updatePopup(layer, areaHectares, areaSquareMeters);
        onPolygonEdited(layerId, coords, areaHectares);
      });
    };

    // Handle polygon deletion
    const onDeleted = (e: any) => {
      const layers = e.layers;
      layers.eachLayer((layer: any) => {
        const layerId = L.Util.stamp(layer);
        onPolygonDeleted(layerId);
      });
    };

    map.on(L.Draw.Event.DRAWSTART, onDrawStart);
    map.on(L.Draw.Event.DRAWSTOP, onDrawStop);
    map.on(L.Draw.Event.EDITSTART, onEditStart);
    map.on(L.Draw.Event.EDITSTOP, onEditStop);
    map.on(L.Draw.Event.DELETESTART, onDeleteStart);
    map.on(L.Draw.Event.DELETESTOP, onDeleteStop);
    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.EDITED, onEdited);
    map.on(L.Draw.Event.DELETED, onDeleted);

    return () => {
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
      }
      map.removeLayer(featureGroupRef.current);
      map.off(L.Draw.Event.DRAWSTART, onDrawStart);
      map.off(L.Draw.Event.DRAWSTOP, onDrawStop);
      map.off(L.Draw.Event.EDITSTART, onEditStart);
      map.off(L.Draw.Event.EDITSTOP, onEditStop);
      map.off(L.Draw.Event.DELETESTART, onDeleteStart);
      map.off(L.Draw.Event.DELETESTOP, onDeleteStop);
      map.off(L.Draw.Event.CREATED, onCreated);
      map.off(L.Draw.Event.EDITED, onEdited);
      map.off(L.Draw.Event.DELETED, onDeleted);
    };
  }, [map, onPolygonCreated, onPolygonEdited, onPolygonDeleted]);

  return null;
};


// Component to fit bounds for multiple territories
const FitBounds: React.FC<{ boundaries: TerritoryBoundary[] }> = ({ boundaries }) => {
  const map = useMap();

  React.useEffect(() => {
    if (boundaries.length === 0) return;

    // Calculate combined bounds from all territories
    const allPoints: [number, number][] = boundaries.flatMap(t => t.boundary.flat());

    if (allPoints.length > 0) {
      const lats = allPoints.map(p => p[0]);
      const lngs = allPoints.map(p => p[1]);

      const bounds: [[number, number], [number, number]] = [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)]
      ];

      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, boundaries]);

  return null;
};

// Component to handle viewport updates and clicks
const MapEventHandler: React.FC<{
  onViewportChange: (bounds: any, zoom: number) => void;
  onMapClick?: (lat: number, lng: number) => void;
}> = ({ onViewportChange, onMapClick }) => {
  const map = useMapEvents({
    click: (e) => {
      // Skip clicks while Leaflet.Draw is actively drawing/editing
      if (drawingActiveRef.current) return;
      if (onMapClick) {
        onMapClick(e.latlng.lat, e.latlng.lng);
      }
    },
    moveend: () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      onViewportChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      }, zoom);
    },
    zoomend: () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      onViewportChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      }, zoom);
    }
  });

  // Trigger initial viewport update on mount
  React.useEffect(() => {
    const bounds = map.getBounds();
    const zoom = map.getZoom();
    onViewportChange({
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest()
    }, zoom);
  }, [map, onViewportChange]);

  return null;
};

export const Map: React.FC<MapProps> = ({
  center,
  boundaries,
  gridCells,
  layers,
  onMapClick,
  selectedTerritoryId,
  onProjectPolygonCreate
}) => {
  const [viewport, setViewport] = useState<{
    bounds: { north: number; south: number; east: number; west: number } | null;
    zoom: number;
  }>({
    bounds: null,
    zoom: 13
  });
  const [projectPolygons, setProjectPolygons] = useState<ProjectPolygon[]>([]);
  const [editingPolygonId, setEditingPolygonId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const polygonCounterRef = useRef(0);

  // Magic Select state
  const [magicSelectActive, setMagicSelectActive] = useState(false);
  const [magicSelectLoading, setMagicSelectLoading] = useState(false);
  const [magicThreshold, setMagicThreshold] = useState(0.4);
  const [magicMode, setMagicMode] = useState<'forest' | 'deforested'>('forest');

  const handleViewportChange = useCallback((bounds: any, zoom: number) => {
    setViewport({ bounds, zoom });
  }, []);

  const handlePolygonCreated = useCallback((coords: [number, number][], areaHectares: number, layerId: number) => {
    if (!selectedTerritoryId) return;

    // Find territory name
    const territory = boundaries.find(b => b.id === selectedTerritoryId);

    polygonCounterRef.current += 1;
    const newPolygon: ProjectPolygon = {
      id: `project-${Date.now()}`,
      layerId,
      name: `Project ${polygonCounterRef.current}`,
      coordinates: coords,
      areaHectares,
      territoryId: selectedTerritoryId,
      territoryName: territory?.name,
      createdAt: new Date()
    };

    setProjectPolygons(prev => [...prev, newPolygon]);

    if (onProjectPolygonCreate) {
      onProjectPolygonCreate(newPolygon);
    }

    console.log('Project polygon created:', newPolygon);
  }, [selectedTerritoryId, onProjectPolygonCreate, boundaries]);

  const handlePolygonEdited = useCallback((layerId: number, coords: [number, number][], areaHectares: number) => {
    setProjectPolygons(prev => prev.map(p =>
      p.layerId === layerId
        ? { ...p, coordinates: coords, areaHectares }
        : p
    ));
    console.log('Project polygon edited:', layerId, areaHectares);
  }, []);

  const handlePolygonDeleted = useCallback((layerId: number) => {
    setProjectPolygons(prev => prev.filter(p => p.layerId !== layerId));
    console.log('Project polygon deleted:', layerId);
  }, []);

  // Rename polygon handler
  const handleRenamePolygon = useCallback((polygonId: string, newName: string) => {
    setProjectPolygons(prev => prev.map(p =>
      p.id === polygonId ? { ...p, name: newName } : p
    ));
    setEditingPolygonId(null);
  }, []);

  // Categorize polygon handler
  const handleCategorizePolygon = useCallback((polygonId: string, category: PolygonCategory) => {
    setProjectPolygons(prev => prev.map(p =>
      p.id === polygonId
        ? { ...p, category: p.category === category ? undefined : category }
        : p
    ));
  }, []);

  // Download KML handler
  const handleDownloadKML = useCallback(() => {
    if (projectPolygons.length === 0) return;

    const territoryName = boundaries.find(b => b.id === selectedTerritoryId)?.name;
    const kmlContent = generateKML(projectPolygons, territoryName);
    const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `project-areas-${selectedTerritoryId || 'all'}-${new Date().toISOString().split('T')[0]}.kml`;
    link.click();
    URL.revokeObjectURL(url);
  }, [projectPolygons, boundaries, selectedTerritoryId]);

  // Download territory boundary KML handler
  const handleDownloadBoundaryKML = useCallback(() => {
    if (boundaries.length === 0) return;

    const kmlContent = generateBoundaryKML(boundaries);
    const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const slug = boundaries.length === 1
      ? boundaries[0].name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || `territory-${boundaries[0].id}`
      : 'territories';
    link.download = `boundary-${slug}-${new Date().toISOString().split('T')[0]}.kml`;
    link.click();
    URL.revokeObjectURL(url);
  }, [boundaries]);

  // Magic select click handler
  const handleMagicSelectClick = useCallback(async (lat: number, lng: number) => {
    if (!magicSelectActive || magicSelectLoading || !selectedTerritoryId) return;

    // Get map viewport bounds for NDVI fetch
    if (!viewport.bounds) return;
    const { north, south, east, west } = viewport.bounds;
    const bbox: [number, number, number, number] = [west, south, east, north];

    setMagicSelectLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_URL}/api/magic-select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bbox,
          clickPoint: { lat, lng },
          threshold: magicThreshold,
          mode: magicMode
        })
      });

      const data = await response.json();

      if (!response.ok) {
        alert(`Magic select error: ${data.error}`);
        return;
      }

      if (!data.polygon) {
        alert(data.message || 'No area detected at this point');
        return;
      }

      // Add the detected polygon as a project polygon
      const territory = boundaries.find(b => b.id === selectedTerritoryId);
      polygonCounterRef.current += 1;

      const newPolygon: ProjectPolygon = {
        id: `magic-${Date.now()}`,
        layerId: Date.now(),
        name: `${magicMode === 'forest' ? 'Forest' : 'Deforested'} ${polygonCounterRef.current}`,
        category: magicMode === 'forest' ? 'forest' : undefined,
        coordinates: data.polygon.coordinates as [number, number][],
        areaHectares: data.polygon.areaHectares,
        territoryId: selectedTerritoryId,
        territoryName: territory?.name,
        createdAt: new Date()
      };

      setProjectPolygons(prev => [...prev, newPolygon]);
      if (onProjectPolygonCreate) onProjectPolygonCreate(newPolygon);
      console.log('Magic select polygon created:', newPolygon.name, data.polygon.areaHectares.toFixed(1), 'ha');
    } catch (err: any) {
      alert(`Magic select failed: ${err.message}. Make sure the API server is running (npm run dev:server).`);
    } finally {
      setMagicSelectLoading(false);
    }
  }, [magicSelectActive, magicSelectLoading, selectedTerritoryId, viewport.bounds, magicThreshold, magicMode, boundaries, onProjectPolygonCreate]);

  // Wrap map click to intercept for magic select
  const handleMapClick = useCallback((lat: number, lng: number) => {
    if (magicSelectActive) {
      handleMagicSelectClick(lat, lng);
    } else if (onMapClick) {
      onMapClick(lat, lng);
    }
  }, [magicSelectActive, handleMagicSelectClick, onMapClick]);

  // Memoized filtered and clustered cells based on viewport and zoom
  const visibleCells = useMemo(() => {
    if (!layers.showGrid) {
      return [];
    }

    if (gridCells.length === 0) {
      return [];
    }

    // Apply clustering based on zoom level
    const clustered = clusterCells(gridCells, viewport.zoom);

    // Only log when zoom changes significantly
    if (viewport.zoom % 2 === 0) {
      console.log(`Zoom: ${viewport.zoom} | Rendered: ${clustered.length} / ${gridCells.length} cells`);
    }
    return clustered;
  }, [gridCells, viewport.zoom, layers.showGrid]);

  return (
    <div className="map-container">
      <MapContainer
        center={center}
        zoom={13}
        className="map"
        zoomControl={false}
        preferCanvas={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.esri.com/">Esri</a> World Imagery'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />

        <ZoomControl position="topright" />
        <FitBounds boundaries={boundaries} />
        <MapEventHandler onViewportChange={handleViewportChange} onMapClick={handleMapClick} />
        <DrawControl
          selectedTerritoryId={selectedTerritoryId}
          onPolygonCreated={handlePolygonCreated}
          onPolygonEdited={handlePolygonEdited}
          onPolygonDeleted={handlePolygonDeleted}
        />

        {/* Territory Boundaries - render all selected territories */}
        {layers.showBoundary && boundaries.map((territory, index) => {
          const colors = BOUNDARY_COLORS[index % BOUNDARY_COLORS.length];
          return (
            <Polygon
              key={territory.id}
              positions={territory.boundary}
              pathOptions={{
                color: colors.stroke,
                weight: 3,
                fillColor: colors.fill,
                fillOpacity: 0.08
              }}
            >
              <Popup>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{territory.name}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Code: {territory.id}</div>
              </Popup>
            </Polygon>
          );
        })}

        {/* Grid Cells - ALWAYS render, bright colors for visibility */}
        {visibleCells.map(cell => (
          <Rectangle
            key={cell.id}
            bounds={cell.bounds}
            pathOptions={{
              color: '#FF0000', // BRIGHT RED border
              weight: 2,
              fillColor: '#FF0000', // BRIGHT RED fill
              fillOpacity: 0.4,
              interactive: true
            }}
            eventHandlers={{
              click: () => {
                console.log('🎯 Cell clicked:', cell.id, 'BQI:', cell.metadata.bqiScore, 'Blockchain:', !!cell.blockchain);
              }
            }}
          >
            <Popup maxWidth={500} minWidth={300}>
              <GridCellPopup cell={cell} />
            </Popup>
          </Rectangle>
        ))}

        {/* Magic-selected polygons (rendered as Leaflet polygons since they're not in DrawControl) */}
        {projectPolygons.filter(p => p.id.startsWith('magic-')).map(polygon => {
          const cat = polygon.category ? CATEGORY_COLORS[polygon.category] : null;
          return (
            <Polygon
              key={polygon.id}
              positions={polygon.coordinates}
              pathOptions={{
                color: cat ? cat.stroke : '#00FF00',
                weight: 3,
                fillColor: cat ? cat.fill : '#00FF00',
                fillOpacity: 0.3
              }}
            >
              <Popup>
                <div style={{ padding: '8px' }}>
                  <strong style={{ color: '#1a2642', fontSize: '14px' }}>{polygon.name}</strong>
                  <div style={{ marginTop: '8px', fontSize: '16px', color: cat ? cat.stroke : '#00AA00', fontWeight: 'bold' }}>
                    {polygon.areaHectares.toLocaleString('en-US', { maximumFractionDigits: 2 })} hectares
                  </div>
                </div>
              </Popup>
            </Polygon>
          );
        })}
      </MapContainer>



      {/* Boundary KML download — visible whenever a territory is selected */}
      {boundaries.length > 0 && (
        <button
          onClick={handleDownloadBoundaryKML}
          title={boundaries.length === 1
            ? `Download "${boundaries[0].name}" boundary as KML`
            : `Download ${boundaries.length} territory boundaries as KML`}
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            background: '#c9a961',
            color: '#1a2642',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 14px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            zIndex: 1000,
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
          }}
        >
          <span style={{ fontSize: '14px' }}>&#x2B07;</span>
          {boundaries.length === 1 ? 'Boundary KML' : `Boundaries KML (${boundaries.length})`}
        </button>
      )}

      {/* Magic Select Panel - positioned beside Project Areas panel */}
      <MagicSelectControl
        active={magicSelectActive}
        loading={magicSelectLoading}
        threshold={magicThreshold}
        mode={magicMode}
        rightOffset={projectPolygons.length > 0 ? 620 : 340}
        onThresholdChange={setMagicThreshold}
        onModeChange={setMagicMode}
        onCancel={() => setMagicSelectActive(false)}
      />

      {/* Project Polygons Panel - positioned beside territory info (bottom-right) */}
      {projectPolygons.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '340px',
          background: 'rgba(26, 38, 66, 0.95)',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '13px',
          zIndex: 1000,
          minWidth: '260px',
          maxWidth: '320px',
          border: '1px solid #c9a961',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h4 style={{ margin: 0, color: '#c9a961' }}>
              Project Areas ({projectPolygons.length})
            </h4>
            <button
              onClick={handleDownloadKML}
              style={{
                background: '#c9a961',
                color: '#1a2642',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Download all project areas as KML"
            >
              <span style={{ fontSize: '14px' }}>&#x2B07;</span> KML
            </button>
          </div>
          {projectPolygons.map((polygon, index) => (
            <div key={polygon.id} style={{
              padding: '6px 0',
              borderBottom: index < projectPolygons.length - 1 ? '1px solid rgba(201, 169, 97, 0.3)' : 'none'
            }}>
              {/* Name row with edit and area */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {editingPolygonId === polygon.id ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onBlur={() => handleRenamePolygon(polygon.id, editName || polygon.name)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRenamePolygon(polygon.id, editName || polygon.name);
                      if (e.key === 'Escape') setEditingPolygonId(null);
                    }}
                    autoFocus
                    style={{
                      background: 'rgba(255,255,255,0.15)',
                      border: '1px solid #c9a961',
                      borderRadius: '3px',
                      color: 'white',
                      padding: '2px 6px',
                      fontSize: '12px',
                      width: '110px',
                      outline: 'none'
                    }}
                  />
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{
                      display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                      background: polygon.category ? CATEGORY_COLORS[polygon.category].stroke : '#00FF00'
                    }} />
                    <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {polygon.name}
                    </span>
                    <button
                      onClick={() => { setEditingPolygonId(polygon.id); setEditName(polygon.name); }}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: '0 2px', fontSize: '12px' }}
                      title="Rename"
                    >&#9998;</button>
                  </span>
                )}
                <span style={{ color: polygon.category ? CATEGORY_COLORS[polygon.category].stroke : '#00FF00', fontWeight: 600, fontSize: '12px' }}>
                  {polygon.areaHectares.toLocaleString('en-US', { maximumFractionDigits: 2 })} ha
                </span>
              </div>
              {/* Category buttons row */}
              <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                {(Object.entries(CATEGORY_COLORS) as [PolygonCategory, typeof CATEGORY_COLORS[PolygonCategory]][]).map(([cat, cfg]) => (
                  <button
                    key={cat}
                    onClick={() => handleCategorizePolygon(polygon.id, cat)}
                    style={{
                      background: polygon.category === cat ? cfg.stroke : 'rgba(255,255,255,0.08)',
                      color: polygon.category === cat ? '#1a2642' : 'rgba(255,255,255,0.6)',
                      border: `1px solid ${polygon.category === cat ? cfg.stroke : 'rgba(255,255,255,0.2)'}`,
                      borderRadius: '3px',
                      padding: '1px 6px',
                      fontSize: '10px',
                      fontWeight: polygon.category === cat ? 700 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >{cfg.label}</button>
                ))}
              </div>
            </div>
          ))}
          <div style={{
            marginTop: '10px',
            paddingTop: '10px',
            borderTop: '1px solid rgba(201, 169, 97, 0.5)',
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 600
          }}>
            <span>Total:</span>
            <span style={{ color: '#00FF00' }}>
              {projectPolygons.reduce((sum, p) => sum + p.areaHectares, 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} ha
            </span>
          </div>
        </div>
      )}

    </div>
  );
};
